(() => {
  const desktopQuery = window.matchMedia('(min-width: 901px)');
  const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Set();
  const controllerBySection = new WeakMap();

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, progress) => from + (to - from) * progress;
  const easeOutCubic = (progress) => 1 - Math.pow(1 - clamp(progress), 3);
  const range = (progress, start, end) => clamp((progress - start) / (end - start));

  const clearStyle = (element, properties) => {
    if (!element) return;
    properties.forEach((property) => element.style.removeProperty(property));
  };

  const createController = (section) => {
    if (
      !section ||
      controllerBySection.has(section) ||
      !desktopQuery.matches ||
      reducedQuery.matches
    ) return null;

    const object = section.querySelector('.anatomy-object');
    const objectImage = section.querySelector('.anatomy-object-image');
    const scan = section.querySelector('.anatomy-object-scan');
    const headingItems = Array.from(section.querySelectorAll('.anatomy-heading > *'));
    const labels = Array.from(section.querySelectorAll('[data-anatomy-label]'));
    const traces = Array.from(section.querySelectorAll('[data-anatomy-trace]'));
    const progressBar = section.querySelector('[data-anatomy-progress]');
    const orbitOne = section.querySelector('.anatomy-orbit-one');
    const orbitTwo = section.querySelector('.anatomy-orbit-two');

    if (!object || !labels.length) return null;

    const traceParts = traces.map((trace) => {
      const line = trace.querySelector('.anatomy-line');
      const markers = Array.from(trace.querySelectorAll('.anatomy-marker'));
      let length = 0;

      try {
        length = line ? Math.ceil(line.getTotalLength()) : 0;
      } catch (_error) {
        length = 0;
      }

      if (line && length) {
        line.style.strokeDasharray = `${length}`;
        line.style.strokeDashoffset = `${length}`;
      }

      return { line, markers, length };
    });

    let frame = 0;
    let destroyed = false;
    let lastProgress = -1;

    const render = () => {
      frame = 0;
      if (destroyed || !document.documentElement.contains(section)) return;

      try {
        const rect = section.getBoundingClientRect();
        const scrollDistance = Math.max(rect.height - window.innerHeight, 1);
        const progress = clamp(-rect.top / scrollDistance);

        if (Math.abs(progress - lastProgress) < 0.0002) return;
        lastProgress = progress;

        const entrance = easeOutCubic(range(progress, 0, 0.16));
        const drift = range(progress, 0.16, 0.76);
        const settle = easeOutCubic(range(progress, 0.76, 0.9));
        const objectScale = settle
          ? lerp(1, 0.985, settle)
          : lerp(0.9, 1, entrance);
        const rotationY = settle
          ? lerp(3.5, 0, settle)
          : lerp(0, 3.5, drift);
        const rotationX = settle
          ? lerp(-1.1, 0, settle)
          : lerp(0, -1.1, drift);

        object.style.opacity = `${lerp(0.68, 1, entrance)}`;
        object.style.transform =
          `perspective(1100px) rotateY(${rotationY}deg) rotateX(${rotationX}deg) scale(${objectScale})`;

        if (objectImage) {
          const imageGrowth = range(progress, 0.05, 0.76);
          const imageSettle = easeOutCubic(range(progress, 0.76, 0.9));
          const imageScale = imageSettle
            ? lerp(1.065, 1.035, imageSettle)
            : lerp(1.03, 1.065, imageGrowth);
          objectImage.style.transform = `translateZ(2.5rem) scale(${imageScale})`;
        }

        if (scan) {
          scan.style.transform = `translate3d(0, ${lerp(-180, 1500, range(progress, 0.03, 0.84))}%, 0)`;
          scan.style.opacity = progress >= 0.88 ? '0' : '1';
        }

        if (orbitOne) {
          orbitOne.style.transform =
            `translate(-50%, -50%) rotate(${lerp(-13, -6, progress)}deg)`;
        }

        if (orbitTwo) {
          orbitTwo.style.transform =
            `translate(-50%, -50%) rotate(${lerp(62, 76, progress)}deg) scaleY(0.48)`;
        }

        if (progressBar) {
          progressBar.style.transform = `scaleY(${lerp(0.04, 1, range(progress, 0.01, 0.84))})`;
        }

        headingItems.forEach((item, index) => {
          const itemProgress = easeOutCubic(range(progress, index * 0.018, 0.12 + index * 0.018));
          item.style.opacity = `${lerp(0.72, 1, itemProgress)}`;
          item.style.transform = `translate3d(0, ${lerp(16, 0, itemProgress)}px, 0)`;
        });

        traceParts.forEach(({ line, markers, length }, index) => {
          const start = 0.2 + index * 0.22;
          const lineProgress = easeOutCubic(range(progress, start, start + 0.17));
          const markerProgress = easeOutCubic(range(progress, start + 0.09, start + 0.19));
          const labelProgress = easeOutCubic(range(progress, start + 0.07, start + 0.2));
          const label = labels[index];

          if (line && length) {
            line.style.strokeDashoffset = `${length * (1 - lineProgress)}`;
          }

          markers.forEach((marker) => {
            marker.style.opacity = `${markerProgress}`;
            marker.style.transform = `scale(${markerProgress})`;
            marker.style.transformOrigin = 'center';
          });

          if (label) {
            label.style.opacity = `${labelProgress}`;
            label.style.transform = `translate3d(0, ${lerp(24, 0, labelProgress)}px, 0)`;
          }
        });
      } catch (_error) {
        controller.destroy();
      }
    };

    const requestRender = () => {
      if (destroyed || frame) return;
      frame = window.requestAnimationFrame(render);
    };

    const resetStyles = () => {
      clearStyle(object, ['opacity', 'transform']);
      clearStyle(objectImage, ['transform']);
      clearStyle(scan, ['opacity', 'transform']);
      clearStyle(orbitOne, ['transform']);
      clearStyle(orbitTwo, ['transform']);
      clearStyle(progressBar, ['transform']);
      headingItems.forEach((item) => clearStyle(item, ['opacity', 'transform']));
      labels.forEach((label) => clearStyle(label, ['opacity', 'transform']));
      traceParts.forEach(({ line, markers }) => {
        clearStyle(line, ['stroke-dasharray', 'stroke-dashoffset']);
        markers.forEach((marker) => {
          clearStyle(marker, ['opacity', 'transform', 'transform-origin']);
        });
      });
    };

    const controller = {
      section,
      requestRender,
      destroy() {
        if (destroyed) return;
        destroyed = true;
        if (frame) window.cancelAnimationFrame(frame);
        window.removeEventListener('scroll', requestRender);
        window.removeEventListener('resize', requestRender);
        window.removeEventListener('orientationchange', requestRender);
        section.removeAttribute('data-anatomy-animated');
        resetStyles();
        controllers.delete(controller);
        controllerBySection.delete(section);
      }
    };

    controllerBySection.set(section, controller);
    controllers.add(controller);
    section.dataset.anatomyAnimated = 'true';
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender, { passive: true });
    window.addEventListener('orientationchange', requestRender, { passive: true });
    requestRender();
    return controller;
  };

  const initialise = (scope = document) => {
    if (!desktopQuery.matches || reducedQuery.matches) return;

    if (scope instanceof Element && scope.matches('[data-anatomy-section]')) {
      createController(scope);
    }
    scope.querySelectorAll('[data-anatomy-section]').forEach(createController);
  };

  const reset = () => {
    Array.from(controllers).forEach((controller) => controller.destroy());
    initialise(document);
  };

  const watchMedia = (query, listener) => {
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', listener);
    } else if (typeof query.addListener === 'function') {
      query.addListener(listener);
    }
  };

  const onReady = () => initialise(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    Array.from(controllers).forEach((controller) => {
      if (event.target === controller.section || event.target.contains(controller.section)) {
        controller.destroy();
      }
    });
  });

  watchMedia(desktopQuery, reset);
  watchMedia(reducedQuery, reset);
})();
